import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import './deals.css';
function Deals() {
  const navigate = useNavigate();
  const [postalCode, setPostalCode] = useState('');
  const [searchRadius, setSearchRadius] = useState(5);
  const [stores, setStores] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedWebsite, setSelectedWebsite] = useState('');
  const [cities, setCities] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loadingDeals, setLoadingDeals] = useState(false);
  const apiBaseUrl = process.env.REACT_APP_RAILWAY_URL || 'http://localhost:8000';

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/search-cities/`);
        const data = await response.json();
        setCities(data.cities || []);
      } catch (err) {
        console.error('Error fetching cities:', err);
      }
    };
    
    if (apiBaseUrl) {
      fetchCities();
    }
  }, [apiBaseUrl]);

  const handlePostalCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
    setPostalCode(value);
  };

  const handleRadiusChange = (e) => {
    setSearchRadius(Number(e.target.value));
  };

  const handleWebsiteChange = (e) => {
    setSelectedWebsite(e.target.value);
  };

  const handleStoreSelect = (store) => {
    setSelectedStores(prev => {
      if (prev.some(s => s.id === store.id)) {
        return prev.filter(s => s.id !== store.id);
      }
      return [...prev, store];
    });
  };

  const fetchDealsForStores = async (storeList) => {
    if (storeList.length === 0) {
      setDeals([]);
      return;
    }

    setLoadingDeals(true);
    try {
      const storeNames = storeList.map(store => store.name);
      const response = await fetch(`${apiBaseUrl}/api/search-deals/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stores: storeNames })
      });

      const data = await response.json();
      
      if (data.success) {
        setDeals(data.deals || []);
      } else {
        console.error('Error fetching deals:', data.error);
        setDeals([]);
      }
    } catch (err) {
      console.error('Error fetching deals:', err);
      setDeals([]);
    } finally {
      setLoadingDeals(false);
    }
  };

  const handleSearch = async () => {
    if (postalCode.length !== 5) {
      setError('Please enter a valid 5-digit postal code');
      return;
    }

    if (!selectedWebsite) {
      setError('Please select a store to search');
      return;
    }

    setLoading(true);
    setError(null);
    setSelectedStores([]);

    try {
      const searchResponse = await fetch(`${apiBaseUrl}/api/get-stores/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          value: selectedWebsite,
          searchItem: postalCode,
          radius: searchRadius
        })
      });

      const searchData = await searchResponse.json();
      
      if (searchData.error) {
        throw new Error(searchData.error);
      }

      if (searchData.items && Array.isArray(searchData.items)) {
        const storeList = searchData.items.map(store => ({
          id: store.url,
          name: store.name,
          address: store.address,
          postal_code: store.postal_code,
          city: store.city,
          distance: parseFloat(store.distance).toFixed(1),
        }));
        setStores(storeList);
        
        // Fetch deals for the found stores
        fetchDealsForStores(storeList);
      }

    } catch (err) {
      setError('Failed to fetch stores. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDeals = async () => {
    if (selectedStores.length === 0) {
      setError('Please select at least one store');
      return;
    }

    setLoading(true);
    try {
      const offersResponse = await fetch(`${apiBaseUrl}/api/search-offers/`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      const offersData = await offersResponse.json();
      
      if (offersData.error) {
        throw new Error(offersData.error);
      }

      navigate('/store-deals', { 
        state: { 
          stores: selectedStores,
          offers: offersData
        } 
      });
    } catch (err) {
      setError('Failed to fetch deals. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="deals-page">
      <div className="deals-search-container">
        <div className="search-input-group">
          <input
            type="text"
            value={postalCode}
            onChange={handlePostalCodeChange}
            placeholder="Enter your postal code"
            maxLength="5"
          />
          <div className="radius-selector">
            <p>Search radius:</p>
            <input
              type="range"
              min="1"
              max="20"
              value={searchRadius}
              onChange={handleRadiusChange}
              className="radius-slider"
            />
            <span>{searchRadius} km</span>
          </div>
          <select 
            value={selectedWebsite} 
            onChange={handleWebsiteChange}
            className="store-select"
          >
            <option value="">Select store</option>
            <option value="ica">ICA</option>
            <option value="coop">Coop</option>
            <option value="hemkop">Hemköp</option>
          </select>
          <button onClick={handleSearch} disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <section className="stores-section">
        <h2>Stores near you</h2>
        {stores.length > 0 ? (
          <>
            <div className="stores-horizontal-list">
              {stores.map(store => (
                <div 
                  key={store.id} 
                  className={`store-card ${selectedStores.some(s => s.id === store.id) ? 'selected' : ''}`}
                  onClick={() => handleStoreSelect(store)}
                >
                  <h3>{store.name}</h3>
                  <p>{store.address}</p>
                  <p>{store.distance}km away</p>
                </div>
              ))}
            </div>
            <button 
              className="view-deals-btn"
              onClick={handleViewDeals}
              disabled={selectedStores.length === 0}
            >
              View Deals ({selectedStores.length} stores selected)
            </button>
          </>
        ) : (
          <div className="stores-placeholder">
            <p>Search for stores using your postal code</p>
          </div>
        )}
      </section>

      <section className="deals-section">
        <h2>Deals near you</h2>
        {loadingDeals ? (
          <div className="deals-loading">
            <p>Loading deals...</p>
          </div>
        ) : deals.length > 0 ? (
          <div className="deals-horizontal-list">
            {deals.map(deal => (
              <div key={deal.id} className="deal-card">
                <div className="deal-header">
                  <span className="deal-store">{deal.store}</span>
                  <h3>{deal.product_name}</h3>
                </div>
                {deal.brand && <p className="deal-brand">{deal.brand}</p>}
                {deal.amount && <p className="deal-amount">{deal.amount}</p>}
                <div className="deal-pricing">
                  {deal.campaign_price && (
                    <span className="campaign-price">{deal.campaign_price}</span>
                  )}
                  {deal.original_price && (
                    <span className="original-price">{deal.original_price}</span>
                  )}
                  {deal.savings && (
                    <span className="savings">Save {deal.savings}</span>
                  )}
                </div>
                <div className="deal-dates">
                  <small>
                    Valid: {new Date(deal.campaign_start).toLocaleDateString()} - {new Date(deal.campaign_end).toLocaleDateString()}
                  </small>
                </div>
                {deal.description && (
                  <p className="deal-description">{deal.description}</p>
                )}
              </div>
            ))}
          </div>
        ) : stores.length > 0 ? (
          <div className="deals-placeholder">
            <p>No deals found for the selected stores</p>
          </div>
        ) : (
          <div className="deals-placeholder">
            <p>Search for stores to see available deals</p>
          </div>
        )}
      </section>

      <section className="cities-section">
        <h2>Stores included in cities</h2>
        <div className="cities-grid">
          {cities.map(city => (
            <div key={city} className="city-card">
              <span>{city}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default Deals;